/**
 * Centralized Chatbot Save Service
 * 
 * This service provides a single source of truth for saving chatbot changes.
 * All components and hooks should use this service instead of directly calling Supabase.
 * 
 * Features:
 * - Automatic settings merging to prevent data loss
 * - Consistent JSON serialization/deserialization
 * - Error handling and validation
 * - Support for partial updates
 */

import { supabase } from '@/integrations/supabase/client';
import { parseSettings, serializeSettings } from '@/utils/settingsUtils';
import { UnifiedChatbotData } from '@/types/builder';

// Sanitize fields_set actions by removing empty IDs and duplicates
const cleanFieldsSetActions = (settings: any) => {
  if (!settings?.ai_chat_config?.offerGoals?.goals) return settings;

  const cleanedGoals = (settings.ai_chat_config.offerGoals.goals || []).map((goal: any) => {
    const cleanedActions = (goal.actions || []).map((action: any) => {
      if (action?.type === 'fields_set' && Array.isArray(action.requiredFields)) {
        const seenIds = new Set<string>();
        const cleanedFields: Array<{ id: string }> = [];

        action.requiredFields.forEach((field: any) => {
          const id = typeof field?.id === 'string' ? field.id.trim() : '';
          if (!id) return; // skip empty
          if (seenIds.has(id)) return; // skip duplicates
          seenIds.add(id);
          cleanedFields.push({ id });
        });

        return { ...action, requiredFields: cleanedFields };
      }
      return action;
    });

    return { ...goal, actions: cleanedActions };
  });

  return {
    ...settings,
    ai_chat_config: {
      ...(settings.ai_chat_config || {}),
      offerGoals: {
        ...(settings.ai_chat_config?.offerGoals || {}),
        goals: cleanedGoals
      }
    }
  };
};

export interface ChatbotUpdateData {
  // Basic chatbot fields
  name?: string;
  description?: string;
  title?: string;
  welcome_message?: string;
  completion_message?: string;
  active?: boolean;
  custom_javascript?: string;
  custom_html?: string;
  operator_name?: string;
  operator_avatar_url?: string;
  company_name?: string;
  company_logo?: string;
  
  // Settings (will be merged with existing)
  settings?: Partial<any>;
  
  // Other fields
  [key: string]: any;
}

export interface SaveChatbotOptions {
  /**
   * If true, merges settings with existing database settings.
   * If false, replaces settings entirely (use with caution).
   * Default: true
   */
  mergeSettings?: boolean;
  
  /**
   * If true, preserves ai_settings field (prevents overwriting).
   * Default: true
   */
  preserveAiSettings?: boolean;
  
  /**
   * Custom error handler
   */
  onError?: (error: Error) => void;
  
  /**
   * Custom success handler
   */
  onSuccess?: (data: any) => void;
}

export interface SaveChatbotResult {
  success: boolean;
  data?: any;
  error?: Error;
}

/**
 * Saves chatbot updates to the database
 * 
 * @param chatbotId - The ID of the chatbot to update
 * @param updates - Partial chatbot data to update
 * @param options - Additional options for saving
 * @returns Promise with save result
 */
export async function saveChatbot(
  chatbotId: string,
  updates: ChatbotUpdateData,
  options: SaveChatbotOptions = {}
): Promise<SaveChatbotResult> {
  const {
    mergeSettings = true,
    preserveAiSettings = true,
    onError,
    onSuccess
  } = options;

  try {
    // Validate chatbot ID
    if (!chatbotId) {
      throw new Error('Chatbot ID is required');
    }

    // Prepare update data
    const updateData: any = { ...updates };

    // Handle settings merging if needed
    if (updates.settings && mergeSettings) {
      console.log('🔄 Merging settings with database to prevent overwrites...');

      // Get current settings from database
      const { data: currentChatbot, error: fetchError } = await supabase
        .from('chatbots')
        .select('settings')
        .eq('id', chatbotId)
        .single();

      if (fetchError) {
        console.error('❌ Error fetching current settings:', fetchError);
        throw fetchError;
      }

      // Parse current settings
      const currentSettings = parseSettings(currentChatbot?.settings);
      console.log('🔄 Parsed current settings from database:', currentSettings);

      // Merge with new settings
      const mergedSettings = {
        ...currentSettings,
        ...updates.settings
      };

      // Sanitize merged settings (remove duplicate/empty requiredFields in fields_set actions)
      const sanitizedSettings = cleanFieldsSetActions(mergedSettings);

      console.log('🔄 Settings merge result:', {
        current: currentSettings,
        updates: updates.settings,
        merged: sanitizedSettings
      });

      // Serialize merged settings
      updateData.settings = serializeSettings(sanitizedSettings);
    } else if (updates.settings) {
      // If not merging, just serialize the provided settings
      const sanitizedSettings = cleanFieldsSetActions(updates.settings);
      updateData.settings = serializeSettings(sanitizedSettings);
    }

    // Preserve ai_settings if requested
    if (preserveAiSettings) {
      delete updateData.ai_settings;
    }

    // Always update the updated_at timestamp
    updateData.updated_at = new Date().toISOString();

    console.log('💾 Saving chatbot updates:', {
      chatbotId,
      updateFields: Object.keys(updateData),
      hasSettings: !!updateData.settings
    });

    // Perform the update
    const { data, error } = await supabase
      .from('chatbots')
      .update(updateData)
      .eq('id', chatbotId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error saving chatbot:', error);
      throw error;
    }

    console.log('✅ Chatbot saved successfully:', {
      chatbotId,
      updatedFields: Object.keys(updateData)
    });

    // Verify saved settings if they were updated
    if (data.settings) {
      const parsedSettings = parseSettings(data.settings);
      console.log('✅ Verified saved settings:', parsedSettings);
    }

    // Call success handler if provided
    if (onSuccess) {
      onSuccess(data);
    }

    return {
      success: true,
      data
    };

  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('❌ Failed to save chatbot:', err);

    // Call error handler if provided
    if (onError) {
      onError(err);
    }

    return {
      success: false,
      error: err
    };
  }
}

/**
 * Saves only chatbot settings (convenience function)
 * 
 * @param chatbotId - The ID of the chatbot
 * @param settings - Settings to update
 * @param options - Additional options
 */
export async function saveChatbotSettings(
  chatbotId: string,
  settings: Partial<any>,
  options: SaveChatbotOptions = {}
): Promise<SaveChatbotResult> {
  return saveChatbot(chatbotId, { settings }, options);
}

/**
 * Saves chatbot basic info (name, description, etc.)
 * 
 * @param chatbotId - The ID of the chatbot
 * @param basicInfo - Basic info fields to update
 * @param options - Additional options
 */
export async function saveChatbotBasicInfo(
  chatbotId: string,
  basicInfo: {
    name?: string;
    description?: string;
    title?: string;
    welcome_message?: string;
    completion_message?: string;
    active?: boolean;
  },
  options: SaveChatbotOptions = {}
): Promise<SaveChatbotResult> {
  return saveChatbot(chatbotId, basicInfo, options);
}




